/**
 * CommonHeader — the admin/moderator screens' entry point to <AppHeader>.
 *
 * Kept as a named wrapper because 16 screens call it with `title`/`onBackClick`;
 * all of the markup and styling now lives in AppHeader, shared with the student
 * area. It supplies the identity block and logout from the auth store, which is
 * the only thing these screens ever needed on top of the bar itself.
 */

import { useTranslation } from 'react-i18next';
import { authService } from '../../services/authentication/auth-service';
import { useAuthStore } from '../../services/authentication/auth-store';
import { AppHeader } from './app-header';
import defaultAvatar from '../../assets/icons/avatars/default-avatar.svg';

interface CommonHeaderProps {
  title: string;
  showBackButton?: boolean;
  onBackClick?: () => void;
  /**
   * What the back control says. Defaults to "Back to Dashboard", which is where
   * it goes from a top-level screen — but several sub-detail screens hand
   * `onBackClick` a handler that returns to a *list* (the bulk-import job
   * detail, the campaign drill-ins), and the button was labelling those as the
   * dashboard too.
   */
  backLabel?: string;
}

export function CommonHeader({ title, showBackButton, onBackClick, backLabel }: CommonHeaderProps) {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);

  return (
    <AppHeader
      title={title}
      onBack={showBackButton && onBackClick ? onBackClick : undefined}
      backLabel={backLabel ?? t('matchModeration.backToDashboard')}
      user={user ? { name: user.name, meta: user.role, avatarUrl: user.avatarPhotoUrl || defaultAvatar } : undefined}
      onSignOut={() => void authService.logout()}
    />
  );
}
